"use client";

import { authApi } from "#/api/authApi";
import { AuthContext } from "#/context/AuthContext";
import { useClient } from "#/hook/useClient";
import type { LoginRequest } from "#/types/auth";
import type { User } from "#/types/user";
import { ClientUserPermission, UserRole } from "#/types/user";
import { logger } from "#/utils/logger";
import { getFirstAllowedRoute } from "#/utils/routeUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseCookies, destroyCookie } from "nookies";
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isBootstrapped, setIsBootstrapped] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { setClient } = useClient();
  const queryClient = useQueryClient();

  const loginMutation = useMutation({
    mutationFn: async (data: LoginRequest) => authApi.login(data),
    onSuccess: (res) => {
      if (!res?.data) {
        throw new Error("Login failed: no user data returned");
      }
      void processLogin(res.data);
    },
    onError: (error) => {
      logger.error("AuthProvider login error:", error);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => authApi.logout(),
    onSuccess: () => {
      // Clear React state
      setUser(null);
      setIsLoggedIn(false);

      // Cancel all active React Query queries FIRST
      void queryClient.cancelQueries();

      // Clear React Query cache
      queryClient.clear();

      // Clear all cookies
      const cookies = parseCookies();
      Object.keys(cookies).forEach((cookieName) => {
        destroyCookie(null, cookieName, { path: "/" });
      });

      // Clear all localStorage
      localStorage.clear();

      // Clear all sessionStorage
      sessionStorage.clear();

      // Hard reload to login page to ensure clean state
      window.location.href = "/login";
    },
    onError: (error) => {
      logger.error("AuthProvider logout error:", error);
    },
  });

  const login = async (data: LoginRequest) => {
    await loginMutation.mutateAsync(data);
  };

  const logout = async () => {
    await logoutMutation.mutateAsync();
  };

  const processLogin = (user: User) => {
    try {
      setIsLoggedIn(true);
      setUser(user);
      if (user.client) {
        setClient(user.client);
      }

      // Use window.location for hard navigation to ensure it works after query cache clear
      if (user.role === UserRole.ADMIN) {
        window.location.href = "/clients";
      } else {
        window.location.href = getFirstAllowedRoute(user.client?.allowedRoutes);
      }
    } catch (error) {
      logger.error("AuthProvider processLogin error:", error);
    }
  };

  const isPublicPage = ["/login", "/register", "/verify"].includes(location.pathname);

  const { data, isFetching } = useQuery({
    enabled: !isPublicPage,
    queryKey: ["user"],
    queryFn: async () => {
      try {
        const { data: userData } = await authApi.me();
        return userData;
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    // On public pages, immediately bootstrap — but don't reset if login is in progress
    if (isPublicPage) {
      if (!isLoggedIn) {
        setIsBootstrapped(true);
        setUser(null);
      }
      return;
    }

    if (isFetching) return;

    if (data) {
      setIsLoggedIn(true);
      setUser(data);
      if (data.client) {
        setClient(data.client);
      }

      setIsBootstrapped(true);
      if (location.pathname === "/login") {
        if (data.role === UserRole.ADMIN) {
          void navigate("/clients");
        } else {
          void navigate(getFirstAllowedRoute(data.client?.allowedRoutes));
        }
      }
    } else {
      setIsLoggedIn(false);
      setUser(null);
      setIsBootstrapped(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching, isPublicPage, location.pathname]);

  const canDelete = (): boolean => {
    return user?.clientPermission === ClientUserPermission.FULL;
  };

  const canWrite = (): boolean => {
    return user?.clientPermission === ClientUserPermission.WRITE || user?.clientPermission === ClientUserPermission.FULL;
  };

  const canRead = (): boolean => {
    return true;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isBootstrapped,
        isLoggedIn,
        login,
        logout,
        processLogin,
        canDelete,
        canWrite,
        canRead,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
